'use client';

import { useTranslation } from '@/hooks/useTranslation';
import BookDemoSection from '@/components/BookDemoSection';
import { FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import { TELECOM_PROBLEM_METRICS } from '@/content/telecom-metrics';
import TelecomHero from './TelecomHero';
import IndustryBattleground from '@/components/industry/IndustryBattleground';
import IndustryChallenges from '@/components/industry/IndustryChallenges';
import PlatformCapabilities from '@/components/industry/PlatformCapabilities';
import IndustryResults from '@/components/industry/IndustryResults';
import { getIndustryContent } from '@/lib/industryContent';
import '@/components/industry/industry-page.css';

export default function TelecomBody() {
  const { t } = useTranslation();
  const C = getIndustryContent('telecom');

  const CHALLENGES = [
    {
      title: t('industries_telecom.the_complaint_flood'),
      desc: t('industries_telecom.hundreds_to_thousands_of_daily_ment'),
    },
    {
      title: t('industries_telecom.the_outage_storm'),
      desc: t('industries_telecom.a_tower_down_triggers_a_social_medi'),
    },
    {
      title: t('industries_telecom.the_billing_spike'),
      desc: t('industries_telecom.festive_season_or_a_plan_revision_e'),
    },
    {
      title: t('industries_telecom.the_broken_journey'),
      desc: t('industries_telecom.a_subscriber_who_complained_on_twit'),
    },
  ];

  const FIFTH_CHALLENGE = {
    title: t('industries_telecom.the_efficiency_wall'),
    desc: t('industries_telecom.repetitive_queries_data_balance_rec'),
  };

  const FAQS = [
    {
      q: t('industries_telecom.what_is_a_telecom_customer_experien'),
      a: t('industries_telecom.one_workspace_for_subscriber_compla'),
    },
    {
      q: t('industries_telecom.how_does_locobuzz_help_reduce_telec'),
      a: t('industries_telecom.signalsense_detects_port_request_in'),
    },
    {
      q: t('industries_telecom.can_locobuzz_handle_high_volume_com'),
      a: t('industries_telecom.yes_crisis_mode_auto_activates_when'),
    },
    {
      q: t('industries_telecom.what_are_contextualpulse_and_signal'),
      a: t('industries_telecom.contextualpulse_finds_why_complaint'),
    },
    {
      q: t('industries_telecom.does_locobuzz_support_trai_complain'),
      a: t('industries_telecom.yes_workflow_automation_aligns_with'),
    },
    {
      q: t('industries_telecom.which_channels_does_locobuzz_monito'),
      a: t('industries_telecom.20_channels_twitter_x_facebook_inst'),
    },
    {
      q: t('industries_telecom.how_quickly_can_a_telecom_brand_go'),
      a: t('industries_telecom.most_telecom_deployments_go_live_wi'),
    },
    {
      q: t('industries_telecom.what_makes_locobuzz_different_from'),
      a: t('industries_telecom.generic_social_tools_are_built_for'),
    },
  ];

  const metricItems = TELECOM_PROBLEM_METRICS.map(({ value, labelKey }) => ({
    value,
    label: t(labelKey),
  }));

  return (
    <div className="industry-page-wrap">
      <span className="sr-only">
        {t('industries_telecom.locobuzz_is_an_indian_ainative_cust')}
      </span>
      <span className="sr-only">
        {t('industries_telecom.telecom_brands_face_the_highest_chu')}
      </span>
      <span className="sr-only">
        {t('industries_telecom.locobuzz_maps_six_purposebuilt_capa')}
      </span>
      <span className="sr-only">
        {t('industries_telecom.telecom_brands_using_locobuzz_repor')}
      </span>

      <TelecomHero />

      <MetricsBand items={metricItems} columns={3} />

      <IndustryBattleground
        eyebrow={t('industries_telecom.battleground_eyebrow')}
        title={t('industries_telecom.battleground_title')}
        titleAccent={t('industries_telecom.battleground_title_accent')}
        paragraph={t('industries_telecom.in_telecom_a_subscriber_can_port_th')}
        imageSrc="/industries/why/telecom.png"
        imageAlt={t('industries_telecom.telecom_customer_sentiment_and_netw')}
        bg="tint"
      />

      <IndustryChallenges
        title={t('industries_telecom.battleground_challenges_title')}
        titleAccent={t('industries_telecom.battleground_challenges_title_accent')}
        items={[...CHALLENGES, FIFTH_CHALLENGE]}
        layout="row5"
        align="center"
        bg="page"
      />

      <PlatformCapabilities
        {...C.capabilities}
        align="center"
        bg="tint"
      />
      <IndustryResults {...C.results} />

      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={
            <>
              {t('faq.everything_you_need_to')}{' '}
              <span className="serif-accent italic">{t('faq.know')}</span>
            </>
          }
          align="center"
        />
        <FaqAccordion items={FAQS} columns={2} name="telecom-faq" />
      </PageSection>

      <BookDemoSection />
    </div>
  );
}
